import {useEffect, useState} from 'react'

export default function useMultiCountryFields({
    form: {
        control,
        formState: {errors},
        setValue,
        watch
    },
    prefix = '',
    regions = [],
    countries = [],
    languages = [],
    sessionData
}) {
    const sessionStoredDataValues = sessionData
    const regionValue = watch(`${prefix}region`) // Watch for changes in the region field
    const countryValue = watch(`${prefix}country`) // Watch for changes in the country field
    const languageValue = watch(`${prefix}language`) // Watch for changes in the language field
    const [filteredCountries, setFilteredCountries] = useState([])
    const [filteredLanguages, setFilteredLanguages] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!regions.length || !countries.length) {
            setLoading(false)
            return
        }

        setLoading(true)

        const updateCountryList = () => {
            const selectedRegion =
                regionValue !== undefined
                    ? regions.find((region) => region.value === regionValue)
                    : regions.find(
                          (region) => region.value === sessionStoredDataValues?.region?.value
                      )

            if (selectedRegion) {
                setFilteredCountries([])
                const updatedCountries =
                    selectedRegion?.countries.map((regionCountry) =>
                        countries.find((country) => country.value === regionCountry.value)
                    ) || []

                const uniqueCountries = [...new Set(updatedCountries.filter(Boolean))]
                setFilteredCountries(uniqueCountries)

                const currentCountry =
                    countryValue !== undefined
                        ? countryValue
                        : sessionStoredDataValues
                        ? sessionStoredDataValues?.country?.value
                        : 'US'
                const isCurrentCountryValid = uniqueCountries.some(
                    (country) => country?.value === currentCountry
                )

                if (!isCurrentCountryValid) {
                    setValue(`${prefix}country`, uniqueCountries[0]?.value || '')
                } else {
                    setValue(`${prefix}country`, currentCountry)
                }
            } else {
                setFilteredCountries([])
                setValue(`${prefix}country`, '')
            }
        }

        const updateLanguageList = () => {
            const selectedCountry =
                countryValue !== undefined
                    ? countries.find((country) => country.value === countryValue)
                    : countries.find(
                          (country) => country.value === sessionStoredDataValues?.country?.value
                      )

            if (selectedCountry) {
                const countryLanguages = selectedCountry?.languages || []

                const extractedLanguages = countryLanguages.flatMap(({value}) => {
                    const values = value.split(',')
                    return values.map((value) => ({value}))
                })

                const updatedLanguages = languages.filter((language) =>
                    extractedLanguages.some(
                        (countryLanguage) => countryLanguage.value === language.value
                    )
                )

                setFilteredLanguages(updatedLanguages)

                const currentLanguage =
                    languageValue !== undefined
                        ? languageValue
                        : sessionStoredDataValues
                        ? sessionStoredDataValues?.language?.value
                        : 'en'

                const isCurrentLanguageValid = updatedLanguages.some(
                    (language) => language.value === currentLanguage
                )

                if (!isCurrentLanguageValid || currentLanguage === 'en') {
                    const defaultLanguage =
                        updatedLanguages.find((language) => language.value !== 'en') ||
                        updatedLanguages[0]
                    setValue(`${prefix}language`, defaultLanguage?.value || '')
                } else {
                    setValue(`${prefix}language`, currentLanguage)
                }
            } else {
                setFilteredLanguages([])
                setValue(`${prefix}language`, '')
            }
        }

        // Set Sessionstored region if present else set default region to North America
        if (sessionStoredDataValues) {
            const sessionStoredRegion = sessionStoredDataValues?.region?.value
            if (typeof regionValue === 'string') {
                if (sessionStoredRegion === regionValue) {
                    setValue(`${prefix}region`, sessionStoredRegion) // Set the session stored region value
                } else {
                    setValue(`${prefix}region`, regionValue)
                }
            } else if (typeof regionValue !== 'string') {
                if (sessionStoredRegion === regionValue?.value) {
                    setValue(`${prefix}region`, sessionStoredRegion) // Set the session stored region value
                } else {
                    setValue(`${prefix}region`, regionValue?.value)
                }
            }
        } else if (typeof regionValue !== 'string' && regions.length) {
            const defaultRegion = regions.find((region) => region.value === 'US') || regions[0]
            setValue(`${prefix}region`, defaultRegion.value) // Set the default region value
        }
        updateCountryList()
        updateLanguageList()

        setLoading(false)
    }, [regionValue, countryValue, regions, countries, languages, prefix, setValue, watch])

    const handleKeyPress = (options, key) => {
        const normalizedKey = key.toLowerCase()
        return options.filter((option) => option.label.toLowerCase().startsWith(normalizedKey))
    }

    const fields = {
        region: {
            name: `${prefix}region`,
            label: 'REGION:',
            defaultValue: sessionStoredDataValues
                ? sessionStoredDataValues?.region?.value
                : regions.find((region) => region.value === 'US'),
            type: 'select',
            options: regions.map((region) => ({value: region.value, label: region.label})),
            error: errors[`${prefix}region`],
            control,
            disabled: false,
            onKeyDown: (e) => {
                if (e.key.length === 1) {
                    const filteredOptions = handleKeyPress(
                        regions.map((region) => ({value: region.value, label: region.label})),
                        e.key
                    )
                    setFilteredCountries(filteredOptions)
                }
            }
        },
        country: {
            name: `${prefix}country`,
            label: 'COUNTRY:',
            defaultValue: sessionStoredDataValues
                ? sessionStoredDataValues?.country?.value
                : countries.find((country) => country.value === 'US'),
            type: 'select',
            options: filteredCountries.map((country) => ({
                value: country?.value,
                label: country?.label
            })),
            error: errors[`${prefix}country`],
            control,
            disabled: filteredCountries.length === 1,
            onKeyDown: (e) => {
                if (e.key.length === 1) {
                    const filteredOptions = handleKeyPress(
                        filteredCountries.map((country) => ({
                            value: country?.value,
                            label: country?.label
                        })),
                        e.key
                    )
                    setFilteredCountries(filteredOptions)
                }
            }
        },
        language: {
            name: `${prefix}language`,
            label: 'LANGUAGE:',
            defaultValue: sessionStoredDataValues
                ? sessionStoredDataValues?.language?.value
                : languages.find((language) => language.value === 'en'),
            type: 'select',
            options: filteredLanguages.map((language) => ({
                value: language?.value,
                label: language?.label
            })),
            error: errors[`${prefix}language`],
            control,
            disabled: filteredLanguages.length === 1,
            onKeyDown: (e) => {
                if (e.key.length === 1) {
                    const filteredOptions = handleKeyPress(
                        filteredLanguages.map((language) => ({
                            value: language?.value,
                            label: language?.label
                        })),
                        e.key
                    )
                    setFilteredLanguages(filteredOptions)
                }
            }
        }
    }

    return {fields, loading}
}
